import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Trash2, Palette } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { Pagination } from "../components/shared/Pagination";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { DriverFormDialog } from "../components/routes/DriverFormDialog";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListState } from "../hooks/useListState";
import { driverStatusMeta } from "../lib/statusMaps";

const PAGE_SIZE = 20;

export default function Drivers() {
  const navigate = useNavigate();
  const [search, setSearch] = useListState("drivers.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [includeDeleted, setIncludeDeleted] = useListState("drivers.includeDeleted", false);
  const [page, setPage] = useListState("drivers.page", 0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["drivers-list", { q: debouncedSearch, includeDeleted, page }],
    queryFn: async () => {
      const { data } = await apiClient.get("/drivers", {
        params: { q: debouncedSearch || undefined, include_deleted: includeDeleted, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      });
      return data;
    },
    placeholderData: keepPreviousData,
    // Always refetch on return so edits made inside a driver show immediately.
    refetchOnMount: "always",
  });
  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const error = listQuery.isError ? getApiErrorMessage(listQuery.error, "Could not load drivers.") : null;

  const filterKey = `${debouncedSearch}|${includeDeleted}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      if (page !== 0) setPage(0);
    }
  }, [filterKey, page, setPage]);

  useEffect(() => { setSelectedIds([]); }, [listQuery.data]);

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => apiClient.delete("/drivers/bulk-delete", { data: { driver_ids: ids } }).then((r) => r.data),
    onSuccess: (data) => {
      if (data.deleted.length) toast.success(`Deleted ${data.deleted.length} driver(s)`);
      if (data.skipped.length) {
        toast.warning(
          `Skipped ${data.skipped.length}: ${data.skipped.map((s) => `${s.name || s.id} (${s.reason})`).join(", ")}`
        );
      }
      if (!data.deleted.length && !data.skipped.length) {
        toast.info("No drivers were deleted (already removed or not found)");
      }
      setBulkDeleteOpen(false);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ["drivers-list"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, "Could not delete selected drivers.")),
  });

  const columns = [
    { key: "id", header: "ID", className: "text-text-muted" },
    { key: "name", header: "Name", render: (r) => r.name || "—" },
    { key: "phone", header: "Phone", subtitle: true },
    { key: "email", header: "Email", render: (r) => r.email || "—" },
    { key: "address", header: "Address", render: (r) => r.address || "—" },
    {
      key: "status", header: "Status",
      render: (r) => { const m = driverStatusMeta(r); return <StatusBadge label={m.label} variant={m.variant} />; },
    },
  ];

  return (
    <div data-testid="drivers-page">
      <PageHeader
        title="Drivers"
        description="Everyone assigned to run delivery and pickup routes."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/drivers/colors")} data-testid="driver-colors-link-button">
              <Palette size={16} strokeWidth={1.5} /> Driver Colors
            </Button>
            <Button variant="accent" onClick={() => setFormOpen(true)} data-testid="driver-create-button">
              <Plus size={16} strokeWidth={2} /> New Driver
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="relative w-full max-w-xs">
          <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9" placeholder="Search name, phone, address..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="driver-search-input"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-text-muted" data-testid="driver-show-deleted-toggle">
          <Checkbox checked={includeDeleted} onCheckedChange={(v) => setIncludeDeleted(Boolean(v))} data-testid="driver-show-deleted-checkbox" />
          Show deleted
        </label>
        {selectedIds.length > 0 && (
          <Button
            variant="danger"
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="driver-bulk-delete-button"
          >
            <Trash2 size={16} strokeWidth={1.5} /> Delete Selected ({selectedIds.length})
          </Button>
        )}
      </div>

      <DataTable
        testId="drivers-table" columns={columns} rows={items} loading={listQuery.isPending} error={error}
        onRetry={() => listQuery.refetch()} onRowClick={(row) => navigate(`/drivers/${row.id}`)}
        emptyTitle="No drivers found" emptyDescription="Try adjusting your search, or add a new driver."
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
      />
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testId="drivers-pagination" />

      <DriverFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={() => { setFormOpen(false); queryClient.invalidateQueries({ queryKey: ["drivers-list"] }); }} />
      <ConfirmDialog
        open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedIds.length} driver${selectedIds.length === 1 ? "" : "s"}?`}
        description="Drivers that still have pending (unactioned) tiffins in the last 7 days will be skipped and kept. The rest are soft-deleted and their login deactivated — you can revive them anytime from the driver's page."
        confirmLabel="Delete Drivers" variant="danger" loading={bulkDeleteMutation.isPending} onConfirm={() => bulkDeleteMutation.mutate(selectedIds)}
        testId="driver-bulk-delete-confirm-dialog"
      />
    </div>
  );
}
